import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ApolloQueryResult } from '@apollo/client/core';
import * as gql from 'gql-query-builder';
import { TranslateService } from '@ngx-translate/core';
import { GraphQlRequestService } from '@gelato-api-ui/core/api/services/graph-ql-request.service';
import { PriceNavigatorListModel } from '@api-ui-app/src/app/price-navigator/prices/types/price-navigator.types';
import { PriceNavigatorVariables } from '@api-ui-app/src/app/price-navigator/prices/types/price-navigator-variables.types';

@Injectable({ providedIn: 'root' })
export class PriceNavigatorGraphQlService {
  private readonly storeFields = ['type', 'name', 'storeId', 'status', 'currency'];
  private readonly variantFields = [
    'id',
    'productId',
    'previewUrl',
    'title',
    'productName',
    'productUid',
    'designId',
    'retailPrice',
    'productionCost',
    'recommendedRetailPrice',
    'recommendedMinRetailPrice',
    'recommendedMaxRetailPrice',
    'connectionStatus',
    'shippingCost',
    'lastChange',
    'variantOptions',
    'pendingPrice',
    'compareAtPrice',
    'pendingCompareAtPrice',
  ];
  private readonly productFields = [
    'name',
    'id',
    'previewUrl',
    'publishedAt',
    'updatedAt',
    'storeId',
    'currency',
    'country',
    {
      variants: this.variantFields,
    },
  ];
  private readonly productsFields = [
    'pageTotal',
    'total',
    {
      results: this.productFields,
    },
  ];

  constructor(
    private readonly graphQlRequestService: GraphQlRequestService,
    private readonly translateService: TranslateService,
  ) {}

  fetchUnpublishedProductsPrices({
    clientId,
    productUids,
    country,
    currency,
    storeIds,
    storeTypes,
  }: PriceNavigatorVariables): Observable<PriceNavigatorListModel> {
    const operationName = 'priceNavigatorList';
    const operationFieldName = 'unpublishedProducts';
    const storeVariables = {
      clientId: { type: 'String', value: clientId },
      ...(storeIds ? { storeIds: { type: '[String!]', value: storeIds } } : null),
      ...(storeTypes ? { storeTypes: { type: '[String!]', value: storeTypes } } : null),
    };
    const productsVariables = {
      country: { type: 'String!', value: country },
      clientId: { type: 'String!', value: clientId },
      ...(storeIds ? { storeIds: { type: '[String!]', value: storeIds } } : null),
      ...(storeTypes ? { storeTypes: { type: '[String!]', value: storeTypes } } : null),
      ...(productUids ? { productUids: { type: '[JSONObject!]!', value: productUids } } : null),
      ...(currency ? { currency: { type: 'String!', value: currency } } : null),
    };
    const query = gql.query({
      operation: operationName,
      fields: [
        {
          operation: 'stores',
          variables: storeVariables,
          fields: this.storeFields,
        },
        {
          operation: operationFieldName,
          variables: productsVariables,
          fields: this.productsFields,
        },
      ],
    });

    return this.graphQlRequestService
      .query(query, `${operationName}:${operationFieldName}`, true)
      .pipe(map(({ data }: ApolloQueryResult<any>) => data.priceNavigatorList));
  }

  fetchECommProductsPrices({
    country,
    currency,
    clientId,
    sorting,
    searchQuery,
    filters,
    productIds,
    storeIds,
    storeTypes,
    offset = 0,
    limit = 1000,
  }: PriceNavigatorVariables): Observable<PriceNavigatorListModel> {
    const operationName = 'priceNavigatorList';
    const operationFieldName = `${productIds && storeIds ? 'ecommProducts' : 'products'}`;
    const storeVariables = {
      clientId: { type: 'String', value: clientId },
      ...(storeIds ? { storeIds: { type: '[String!]', value: storeIds } } : null),
      ...(storeTypes ? { storeTypes: { type: '[String!]', value: storeTypes } } : null),
    };
    const productsVariables = {
      country: { type: 'String!', value: country },
      clientId: { type: 'String!', value: clientId },
      offset: { type: 'Int!', value: offset },
      limit: { type: 'Float!', value: limit },
      onlyBasicVariantFields: { type: 'Boolean', value: true },
      onlyBasicProductFields: { type: 'Boolean', value: true },
      ...(storeIds ? { storeIds: { type: '[String!]', value: storeIds } } : null),
      ...(storeTypes ? { storeTypes: { type: '[String!]', value: storeTypes } } : null),
      ...(productIds ? { productIds: { type: '[String!]', value: productIds } } : null),
      ...(searchQuery ? { searchQuery: { type: 'String!', value: searchQuery } } : null),
      ...(currency ? { currency: { type: 'String!', value: currency } } : null),
      ...(sorting ? { sorting: { type: 'Sort!', value: sorting } } : null),
      ...(filters ? { filters: { type: 'FiltersExpression', value: { ...filters } } } : null),
    };

    const query = gql.query({
      operation: operationName,
      fields: [
        {
          operation: 'stores',
          variables: storeVariables,
          fields: this.storeFields,
        },
        {
          operation: operationFieldName,
          variables: productsVariables,
          fields: this.productsFields,
        },
      ],
    });

    return this.graphQlRequestService
      .query(query, `${operationName}:${operationFieldName}`, true)
      .pipe(map(({ data }: ApolloQueryResult<any>) => data.priceNavigatorList));
  }

  fetchFiltersData(clientId: string): Observable<any> {
    const variables = {
      clientId: { type: 'String!', value: clientId },
      locale: { type: 'String!', value: this.translateService.currentLang },
    };

    const query = gql.query({
      operation: 'priceNavigatorFilters',
      fields: [
        {
          operation: 'productTypes',
          variables,
          fields: ['id', 'name', 'options'],
        },
      ],
    });

    return this.graphQlRequestService
      .query(query, 'priceNavigatorFilters', true)
      .pipe(map(({ data }: ApolloQueryResult<any>) => data.priceNavigatorFilters));
  }
}
